import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { UnselectAllScandataModels, UnselectScandataModel } from '../../scandata/scandata.actions';
import { PointcloudAPIStatus, PointcloudType, ScandataModel } from '../../scandata/scandata.models';
import { ScandataState } from '../../scandata/scandata.state';
import { Role } from '../../user/user.models';
import { UserState } from '../../user/user.state';
import { DownloadSelectComponent } from '../download/download-select/download-select.component';
import { MultiSelectedComponent } from './multi-selected/multi-selected.component';
import { SingleSelectedComponent } from './single-selected/single-selected.component';

@UntilDestroy()
@Component({
  selector: 'sd-selected-details',
  imports: [CommonModule, MultiSelectedComponent, SingleSelectedComponent, DownloadSelectComponent],
  templateUrl: './selected-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedDetailsComponent {
  private readonly store = inject(Store);
  private readonly userRole = this.store.selectSignal(UserState.userRole);
  private readonly allowEdit = computed(() => {
    const role = this.userRole();
    return role === Role.Admin || role === Role.FullAccess;
  });

  readonly showDownload = signal(false); // set from template

  readonly showSingleSelected = computed(
    () => this.selected().length === 1 && !this.showDownload(),
  );

  readonly singleSelected = computed(() => {
    const selected = this.selected();
    return selected.length === 1 ? selected[0] : null;
  });
  readonly singleSelectedDisableDelete = computed(
    () => !this.allowEdit() || this.singleSelected()?.type === PointcloudType.MobileMappingRun,
  );
  readonly singleSelectedDisableEdit = computed(
    () => !this.allowEdit() || this.singleSelected()?.status !== PointcloudAPIStatus.Ready,
  );

  readonly showMultiSelected = computed(() => this.selected().length > 1 && !this.showDownload());
  readonly selected = this.store.selectSignal(ScandataState.chronologicalSelected);
  readonly multiSelectedReadonly = computed(() => !this.allowEdit());

  unselectScan(scan: ScandataModel) {
    this.store.dispatch(new UnselectScandataModel(scan.id));
  }

  unselectAllScans() {
    this.store.dispatch(new UnselectAllScandataModels());
  }
}
